import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  Clock,
  Settings,
  BarChart3,
  BookOpen,
  FileText,
  PenTool,
  TrendingUp,
  Headphones,
  Monitor,
  ChevronRight,
  Users,
  Target,
  MessageCircle,
  Workflow,
  Swords,
  Map,
  Package,
  Brain,
  Mail,
  TreeDeciduous,
  Stethoscope,
  ExternalLink,
  Sparkles,
  Waves,
  FlaskConical,
  Mountain,
} from 'lucide-react';
import { FEATURE_FLAGS } from '../utils/featureFlags';
import { BRAND_OPTIONS, BrandKey } from '../config/brands';
import { useBrand } from '../context/BrandContext';

interface NavigationProps {
  onItemClick?: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ onItemClick }) => {
  const { brand, brandKey, getPath, switchBrand } = useBrand();
  const [isBrandMenuOpen, setIsBrandMenuOpen] = useState(false);
  const brandSwitcherRef = useRef<HTMLDivElement | null>(null);
  const navStoragePrefix = `${brand.storagePrefix}_nav`;
  const brandNavVisibility = FEATURE_FLAGS.brandNavigationVisibility[brandKey];

  const [showBlogSubMenu, setShowBlogSubMenu] = useState(() => {
    try {
      const saved = localStorage.getItem(`${navStoragePrefix}_blog_submenu`);
      return saved !== null ? JSON.parse(saved) : true;
    } catch (error) {
      console.error('Error parsing localStorage for blog submenu:', error);
      return true;
    }
  });
  const [showRedditAgentsSubMenu, setShowRedditAgentsSubMenu] = useState(() => {
    try {
      const saved = localStorage.getItem(`${navStoragePrefix}_reddit_submenu`);
      return saved !== null ? JSON.parse(saved) : true;
    } catch (error) {
      console.error('Error parsing localStorage for reddit submenu:', error);
      return true;
    }
  });
  const [showNewsletterAgentsSubMenu, setShowNewsletterAgentsSubMenu] = useState(() => {
    try {
      const saved = localStorage.getItem(`${navStoragePrefix}_newsletter_submenu`);
      return saved !== null ? JSON.parse(saved) : true;
    } catch (error) {
      console.error('Error parsing localStorage for newsletter submenu:', error);
      return true;
    }
  });
  const [showKitsSection, setShowKitsSection] = useState(() => {
    try {
      const saved = localStorage.getItem(`${navStoragePrefix}_kits_section`);
      return saved !== null ? JSON.parse(saved) : true;
    } catch (error) {
      console.error('Error parsing localStorage for kits section:', error);
      return true;
    }
  });
  const [showKnowledgeBaseSection, setShowKnowledgeBaseSection] = useState(() => {
    try {
      const saved = localStorage.getItem(`${navStoragePrefix}_kb_section`);
      return saved !== null ? JSON.parse(saved) : true;
    } catch (error) {
      console.error('Error parsing localStorage for kb section:', error);
      return true;
    }
  });

  /**
   * Persist blog submenu state for the active brand.
   * Why this matters: each brand should remember its own nav state instead of sharing one global menu preference.
   */
  const handleBlogAgentsInteraction = (show: boolean) => {
    setShowBlogSubMenu(show);
    localStorage.setItem(`${navStoragePrefix}_blog_submenu`, JSON.stringify(show));
  };

  /**
   * Persist reddit submenu state for the active brand.
   * Why this matters: state should stay scoped to the current brand workspace.
   */
  const handleRedditAgentsInteraction = (show: boolean) => {
    setShowRedditAgentsSubMenu(show);
    localStorage.setItem(`${navStoragePrefix}_reddit_submenu`, JSON.stringify(show));
  };

  /**
   * Persist lifecycle submenu state for the active brand.
   * Why this matters: it prevents menu settings from leaking between brand workspaces.
   */
  const handleNewsletterAgentsInteraction = (show: boolean) => {
    setShowNewsletterAgentsSubMenu(show);
    localStorage.setItem(`${navStoragePrefix}_newsletter_submenu`, JSON.stringify(show));
  };

  /**
   * Toggle the kits section for the active brand.
   * Why this matters: even shell UI preferences should be namespaced now that the app is multi-brand.
   */
  const toggleKitsSection = () => {
    const nextValue = !showKitsSection;
    setShowKitsSection(nextValue);
    localStorage.setItem(`${navStoragePrefix}_kits_section`, JSON.stringify(nextValue));
  };

  /**
   * Toggle the knowledge-base section for the active brand.
   * Why this matters: it keeps the new route namespace and local preferences aligned.
   */
  const toggleKnowledgeBaseSection = () => {
    const nextValue = !showKnowledgeBaseSection;
    setShowKnowledgeBaseSection(nextValue);
    localStorage.setItem(`${navStoragePrefix}_kb_section`, JSON.stringify(nextValue));
  };

  /**
   * Switch to another brand while preserving the current route.
   * Why this matters: the new selector should behave like an app switcher, not a home-page reset.
   */
  const handleBrandSelect = (targetBrandKey: BrandKey) => {
    if (!FEATURE_FLAGS.brandAvailability[targetBrandKey]) {
      return;
    }

    setIsBrandMenuOpen(false);
    switchBrand(targetBrandKey);
    onItemClick?.();
  };

  /**
   * Close the brand menu only when the user clicks outside it or presses Escape.
   * Why this matters: hover should open the selector, but small mouse movements
   * should not make the menu disappear while the user is trying to choose a brand.
   */
  useEffect(() => {
    if (!isBrandMenuOpen) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!brandSwitcherRef.current?.contains(event.target as Node)) {
        setIsBrandMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsBrandMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isBrandMenuOpen]);

  return (
    <nav className="navigation navigation-expanded">
      <div className="nav-logo-section">
        <Link to={getPath()} className="us-citrus-logo-container" style={{ display: 'block' }} onClick={onItemClick}>
          {brand.logoSrc ? (
            <img src={brand.logoSrc} alt={brand.displayName} className="us-citrus-logo-full" />
          ) : (
            <div className="brand-wordmark-fallback">
              <span className="brand-wordmark-title">{brand.shortName}</span>
              <span className="brand-wordmark-domain">{brand.domain}</span>
            </div>
          )}
        </Link>
      </div>

      <div
        ref={brandSwitcherRef}
        className="brand-switcher"
        onMouseEnter={() => setIsBrandMenuOpen(true)}
      >
        <button
          type="button"
          className="brand-switcher-trigger"
          onClick={() => setIsBrandMenuOpen((current) => !current)}
          aria-haspopup="menu"
          aria-expanded={isBrandMenuOpen}
        >
          <div className="brand-switcher-copy">
            <span className="brand-switcher-title">{brand.displayName}</span>
            <span className="brand-switcher-domain">{brand.domain}</span>
          </div>
          <ChevronRight className={`brand-switcher-icon ${isBrandMenuOpen ? 'rotated' : ''}`} />
        </button>

        {isBrandMenuOpen && (
          <div className="brand-switcher-menu" role="menu" aria-label="Brand selector">
            {BRAND_OPTIONS.map((brandOption) => {
              const isBrandAvailable = FEATURE_FLAGS.brandAvailability[brandOption.key];
              const menuLabel = isBrandAvailable
                ? brandOption.displayName
                : `${brandOption.displayName} (Coming Soon)`;

              return (
              <button
                key={brandOption.key}
                type="button"
                className={`brand-switcher-option ${brandOption.key === brandKey ? 'active' : ''} ${!isBrandAvailable ? 'disabled' : ''}`}
                onClick={() => handleBrandSelect(brandOption.key)}
                disabled={!isBrandAvailable}
                aria-disabled={!isBrandAvailable}
              >
                <span className="brand-switcher-option-title">{menuLabel}</span>
                <span className="brand-switcher-option-domain">{brandOption.domain}</span>
              </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="nav-menu">
        <div className="nav-section nav-section-main">
          {FEATURE_FLAGS.showAllNavItems && (
            <div className="nav-item-with-submenu">
              <div className="nav-item nav-item-unclickable" onClick={() => handleRedditAgentsInteraction(!showRedditAgentsSubMenu)}>
                <MessageCircle className="nav-icon" />
                Reddit Agents
                <ChevronRight className={`nav-submenu-icon ${showRedditAgentsSubMenu ? 'rotated' : ''}`} />
              </div>

              {showRedditAgentsSubMenu && (
                <div className="nav-submenu">
                  <NavLink to={getPath('app')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <BarChart3 className="nav-icon" />
                    Subreddit Analyzer
                  </NavLink>
                  <NavLink to={getPath('uncover')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <Target className="nav-icon" />
                    Uncover Themes
                  </NavLink>
                  <NavLink to={getPath('reddit-analysis-history')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <Clock className="nav-icon" />
                    Analysis History
                  </NavLink>
                </div>
              )}
            </div>
          )}

          <div className="nav-item-with-submenu">
            <div className="nav-item nav-item-unclickable" onClick={() => handleBlogAgentsInteraction(!showBlogSubMenu)}>
              <PenTool className="nav-icon" />
              Blog Agents
              <ChevronRight className={`nav-submenu-icon ${showBlogSubMenu ? 'rotated' : ''}`} />
            </div>

            {showBlogSubMenu && (
              <div className="nav-submenu">
                {brandNavVisibility.showRewriteBlogPosts && (
                  <NavLink to={getPath('rewrite-blog-posts')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <PenTool className="nav-icon" />
                    Rewrite Old Blog Posts
                  </NavLink>
                )}
                {brandNavVisibility.showCreateCitrusTreeArticles && (
                  <NavLink to={getPath('create-tree-articles')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <Workflow className="nav-icon" />
                    Create Citrus Tree Articles
                  </NavLink>
                )}
                {brandNavVisibility.showPeopleAlsoAskAgent && (
                  <NavLink to={getPath('people-also-ask-agent')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <MessageCircle className="nav-icon" />
                    People Also Ask
                  </NavLink>
                )}
                {brandNavVisibility.showNewTopicsAgent && (
                  <NavLink to={getPath('new-topics-agent')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <Sparkles className="nav-icon" />
                    New Topics Agent
                  </NavLink>
                )}
                <NavLink to={getPath('ckaa-articles-agent')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                  <Waves className="nav-icon" />
                  CKAA Articles Agent
                </NavLink>
                <NavLink to={getPath('psb-articles-agent')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                  <FlaskConical className="nav-icon" />
                  PSB Articles Agent
                </NavLink>
                <NavLink to={getPath('ss-articles-agent')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                  <Mountain className="nav-icon" />
                  SS Articles Agent
                </NavLink>
                <NavLink to={getPath('tofu-articles-agent')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                  <BookOpen className="nav-icon" />
                  TOFU 100 Articles
                </NavLink>
                {brandNavVisibility.showBestOfArticlesAgent && (
                  <NavLink to={getPath('best-of-articles-agent')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <Sparkles className="nav-icon" />
                    Best Of Articles Agent
                  </NavLink>
                )}
                {FEATURE_FLAGS.showCompetitorConquesting && (
                  <NavLink to={getPath('competitor-conquesting')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <Swords className="nav-icon" />
                    Outrank Competitors
                  </NavLink>
                )}
                {FEATURE_FLAGS.showBlogHistory && (
                  <NavLink to={getPath('blog-history')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <Clock className="nav-icon" />
                    History
                  </NavLink>
                )}
              </div>
            )}
          </div>

          {brandNavVisibility.showCitrusQuiz && (
            <a href={getPath('quiz')} target="_blank" rel="noopener noreferrer" className="nav-item nav-item-external" onClick={onItemClick}>
              {brandKey === 'drmanismagic' ? (
                <Sparkles className="nav-icon" />
              ) : (
                <TreeDeciduous className="nav-icon" />
              )}
              {brandKey === 'drmanismagic' ? 'Gardener Quiz' : 'Citrus Quiz'}
              <ExternalLink className="nav-external-icon" />
            </a>
          )}

          {brandNavVisibility.showCitrusDoctor && (
            <a href={getPath('citrus-doctor')} target="_blank" rel="noopener noreferrer" className="nav-item nav-item-external" onClick={onItemClick}>
              <Stethoscope className="nav-icon" />
              Citrus Doctor
              <ExternalLink className="nav-external-icon" />
            </a>
          )}

          {FEATURE_FLAGS.showCRO && (
            <NavLink to={getPath('cro')} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
              <TrendingUp className="nav-icon" />
              Conversion Rate Optimizer
            </NavLink>
          )}

          {FEATURE_FLAGS.showGongAnalysis && (
            <NavLink to={getPath('gong-analysis')} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
              <Headphones className="nav-icon" />
              Gong Call Analyzer
            </NavLink>
          )}

          {FEATURE_FLAGS.showLandingPageAnalyzer && (
            <NavLink to={getPath('landing-page-analyzer')} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
              <Monitor className="nav-icon" />
              Landing Page CRO Analyzer
            </NavLink>
          )}

          {FEATURE_FLAGS.showAllNavItems && (
            <div className="nav-item-with-submenu">
              <div className="nav-item nav-item-unclickable" onClick={() => handleNewsletterAgentsInteraction(!showNewsletterAgentsSubMenu)}>
                <Mail className="nav-icon" />
                Lifecycle Agents
                <ChevronRight className={`nav-submenu-icon ${showNewsletterAgentsSubMenu ? 'rotated' : ''}`} />
              </div>

              {showNewsletterAgentsSubMenu && (
                <div className="nav-submenu">
                  <NavLink to={getPath('lifecycle-agents')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <Mail className="nav-icon" />
                    Create Email Sequences
                  </NavLink>
                  <NavLink to={getPath('mds-email-templates')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <FileText className="nav-icon" />
                    MDS: Email Templates
                  </NavLink>
                </div>
              )}
            </div>
          )}

          {FEATURE_FLAGS.showAllNavItems && (
            <NavLink to={getPath('voice-of-customer-agent')} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
              <Users className="nav-icon" />
              VoC Agent
            </NavLink>
          )}

          {FEATURE_FLAGS.showAllNavItems && (
            <NavLink to={getPath('cta-creator')} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
              <Target className="nav-icon" />
              CTA Creator
            </NavLink>
          )}

          {FEATURE_FLAGS.showAllNavItems && (
            <NavLink to={getPath('playbooks-creator')} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
              <FileText className="nav-icon" />
              Playbook Creator
            </NavLink>
          )}
        </div>

        <div className="nav-section-bottom">
          {FEATURE_FLAGS.showKitsSection && (
            <div className="nav-section">
              <div className="nav-item-with-submenu">
                <div className="nav-item nav-item-unclickable" onClick={toggleKitsSection}>
                  <Package className="nav-icon" />
                  <span>Kits</span>
                  <ChevronRight className={`nav-submenu-icon ${showKitsSection ? 'rotated' : ''}`} />
                </div>
              </div>
              {showKitsSection && (
                <div className="nav-submenu">
                  <NavLink to={getPath('brand-kit')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <BookOpen className="nav-icon" />
                    Brand Kit
                  </NavLink>

                  {FEATURE_FLAGS.showVoCKit && (
                    <NavLink to={getPath('voc-kit')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                      <Users className="nav-icon" />
                      VoC Kit
                    </NavLink>
                  )}
                </div>
              )}
            </div>
          )}

          {FEATURE_FLAGS.showKnowledgeBaseSection && (
            <div className="nav-section">
              <div className="nav-item-with-submenu">
                <div className="nav-item nav-item-unclickable" onClick={toggleKnowledgeBaseSection}>
                  <Brain className="nav-icon" />
                  <span>Knowledge Base</span>
                  <ChevronRight className={`nav-submenu-icon ${showKnowledgeBaseSection ? 'rotated' : ''}`} />
                </div>
              </div>
              {showKnowledgeBaseSection && (
                <div className="nav-submenu">
                  <NavLink to={getPath('knowledge-base/sitemap')} className={({ isActive }) => `nav-submenu-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                    <Map className="nav-icon" />
                    Sitemap
                  </NavLink>
                </div>
              )}
            </div>
          )}

          {FEATURE_FLAGS.showAllNavItems && (
            <div className="nav-section">
              <NavLink to={getPath('settings')} className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`} onClick={onItemClick}>
                <Settings className="nav-icon" />
                <span>Settings</span>
              </NavLink>
            </div>
          )}
        </div>
      </div>

      <style>
        {`
          .nav-item-with-submenu {
            position: relative;
          }

          .brand-switcher {
            position: relative;
            margin: 0 1rem 1rem;
          }

          .brand-switcher-trigger {
            width: 100%;
            border: 1px solid #d1d5db;
            background: #ffffff;
            border-radius: 0.75rem;
            padding: 0.875rem 1rem;
            display: flex;
            align-items: center;
            gap: 0.75rem;
            text-align: left;
            cursor: pointer;
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
          }

          .brand-switcher-trigger:hover {
            border-color: #9ca3af;
            background: #f9fafb;
          }

          .brand-switcher-copy {
            display: flex;
            flex-direction: column;
            min-width: 0;
          }

          .brand-switcher-title,
          .brand-switcher-option-title {
            font-size: 0.75rem;
            font-weight: 600;
            color: #111827;
          }

          .brand-switcher-domain,
          .brand-switcher-option-domain {
            font-size: 0.75rem;
            color: #6b7280;
            word-break: break-word;
          }

          .brand-switcher-icon,
          .nav-submenu-icon {
            width: 1rem;
            height: 1rem;
            margin-left: auto;
          }

          .brand-switcher-icon.rotated,
          .nav-submenu-icon.rotated {
            transform: rotate(90deg);
          }

          .brand-switcher-menu {
            position: absolute;
            top: calc(100% + 0.5rem);
            left: 0;
            right: 0;
            background: #ffffff;
            border: 1px solid #e5e7eb;
            border-radius: 0.75rem;
            box-shadow: 0 12px 30px rgba(17, 24, 39, 0.12);
            padding: 0.5rem;
            z-index: 20;
          }

          .brand-switcher-option {
            width: 100%;
            border: none;
            background: transparent;
            border-radius: 0.625rem;
            padding: 0.75rem;
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            gap: 0.125rem;
            text-align: left;
            cursor: pointer;
          }

          .brand-switcher-option:hover,
          .brand-switcher-option.active {
            background: #f3f4f6;
          }

          .brand-switcher-option.disabled {
            opacity: 0.55;
            cursor: not-allowed;
          }

          .brand-switcher-option.disabled:hover {
            background: transparent;
          }

          .brand-wordmark-fallback {
            display: flex;
            flex-direction: column;
            gap: 0.125rem;
            padding: 0.25rem 0;
          }

          .brand-wordmark-title {
            font-size: 1rem;
            font-weight: 700;
            color: #111827;
          }

          .brand-wordmark-domain {
            font-size: 0.75rem;
            color: #6b7280;
          }

          .nav-submenu {
            margin-left: 1rem;
            border-left: 2px solid #e5e7eb;
            padding-left: 0.75rem;
            margin-top: 0.5rem;
            animation: slideDown 0.2s ease-out;
          }

          .nav-submenu-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.5rem 1rem;
            color: #6b7280;
            text-decoration: none;
            border-radius: 0.5rem;
            font-size: 0.75rem;
            margin-bottom: 0.25rem;
          }

          .nav-submenu-item:hover {
            background-color: #f3f4f6;
            color: #111827;
          }

          .nav-submenu-item.active {
            background-color: #f3f4f6;
            color: #111827;
            font-weight: 600;
          }

          .nav-submenu-item .nav-icon {
            width: 0.875rem;
            height: 0.875rem;
          }

          .nav-item-unclickable {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem 1rem;
            color: #374151;
            text-decoration: none;
            border-radius: 0.5rem;
            cursor: default;
          }

          .nav-section-content {
            animation: slideDown 0.2s ease-out;
          }

          @keyframes slideDown {
            from {
              opacity: 0;
              transform: translateY(-0.5rem);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .nav-item-external {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem 1rem;
            color: #374151;
            text-decoration: none;
            border-radius: 0.5rem;
            transition: background-color 0.15s ease;
          }

          .nav-item-external:hover {
            background-color: #f3f4f6;
            color: #111827;
          }

          .nav-external-icon {
            width: 0.75rem;
            height: 0.75rem;
            margin-left: auto;
            opacity: 0.5;
          }

          .nav-item-external:hover .nav-external-icon {
            opacity: 1;
          }

          @media (max-width: 768px) {
            .brand-switcher-trigger,
            .nav-submenu-item,
            .nav-item-external,
            .nav-item-unclickable {
              min-height: 3rem;
              padding: 0.875rem 1rem;
            }

            .brand-switcher {
              margin-bottom: 0.75rem;
            }

            .nav-submenu-item {
              font-size: 0.875rem;
            }

            .nav-item-unclickable {
              cursor: pointer;
            }
          }
        `}
      </style>
    </nav>
  );
};

export default Navigation;